import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import PageHero from '../../components/PageHero';
import './PublicationDetailsPage.css';
import publicationsHeroImg from '../../Assets/Image/Publications.webp';
import SEO from '../../components/SEO';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

function resolveImageUrl(url) {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${API_BASE_URL}${url}`;
}

const PublicationDetailsPage = () => {
  const { id } = useParams();
  const [publication, setPublication] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPublication = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/publications/${id}`);
        setPublication(res.data);
      } catch (err) {
        setPublication(null);
      } finally {
        setLoading(false);
      }
    };
    fetchPublication();
  }, [id]);

  if (loading) {
    return (
      <div className="publication-detail-page">
        <SEO title="Loading publication…" noIndex />
        <PageHero title="Loading…" imagePath={publicationsHeroImg} />
        <section className="publication-detail-content">
          <div className="container">
            <p>Loading publication…</p>
          </div>
        </section>
      </div>
    );
  }

  if (!publication) {
    return (
      <div className="publication-detail-page">
        <SEO title="Publication not found" noIndex />
        <PageHero title="Publication Not Found" imagePath={publicationsHeroImg} />
        <section className="publication-detail-content">
          <div className="container">
            <div className="publication-not-found">
              <p>We could not find the publication you are looking for.</p>
              <Link to="/publications" className="back-link">
                ← Back to Publications
              </Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  const heroImagePath = publication.heroImage
    ? resolveImageUrl(publication.heroImage)
    : publicationsHeroImg;

  return (
    <div className="publication-detail-page">
      <SEO
        title={publication.title}
        description={publication.summary}
        image={publication.heroImage ? resolveImageUrl(publication.heroImage) : '/logo.webp'}
        canonicalPath={`/publications/${publication.id || id}`}
        type="article"
      />
      <PageHero title={publication.title} imagePath={heroImagePath} />

      <section
        className="publication-detail-content"
      >
        <div className="container">
          <div className="publication-detail-header">
            <div className="publication-detail-meta">
              <span className="publication-detail-category">{publication.category}</span>
              <span className="publication-detail-date">{publication.date}</span>
            </div>
            <Link to="/publications" className="back-link">
              ← Back to Publications
            </Link>
          </div>

          <div className="publication-detail-layout">
            <article className="publication-detail-article">
              {publication.sections &&
                publication.sections.map((section, index) => {
                  const sectionImageUrl = section.image
                    ? resolveImageUrl(section.image)
                    : null;
                  return (
                  <div key={index} className="publication-section">
                    {section.heading && (
                      <h2 className="publication-section-heading">{section.heading}</h2>
                    )}

                    {sectionImageUrl && (
                      <div className="publication-section-image">
                        <img src={sectionImageUrl} alt={section.heading || publication.title} />
                      </div>
                    )}

                    {section.paragraphs &&
                      section.paragraphs.map((paragraph, pIndex) => (
                        <p key={pIndex} className="publication-section-paragraph">
                          {paragraph}
                        </p>
                      ))}
                  </div>
                  );
                })}
            </article>

            <aside className="publication-detail-aside">
              <div className="publication-highlight-box">
                <h3>At a Glance</h3>
                <p>{publication.summary}</p>
              </div>

              <div className="publication-meta-box">
                <h4>Key Information</h4>
                <ul>
                  <li>
                    <span>Category</span>
                    <strong>{publication.category}</strong>
                  </li>
                  <li>
                    <span>Published</span>
                    <strong>{publication.date}</strong>
                  </li>
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PublicationDetailsPage;

